import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, UserPlus, UserCheck, Loader2, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Follow / Followers 리스트 페이지
 * 라우트:
 *  - /timeline/:userId/followers
 *  - /timeline/:userId/following
 * Prop `kind`로 어떤 리스트인지 결정
 */
export default function FollowListPage({ kind = "followers" }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me, token } = useAuth();
  const isMe = me && userId === me.id;

  const [profile, setProfile] = useState(null);
  const [list, setList] = useState([]);
  const [statusMap, setStatusMap] = useState({}); // { [otherId]: { i_follow, ... } }
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const title = kind === "followers" ? "팔로워" : "팔로잉";

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const fetchProfile = fetch(`${API_URL}/api/users/${userId}/public-profile`).then((r) => r.ok ? r.json() : null);
    // For now use me-only endpoints; for others we still need data.
    // Since we don't have a public followers/following endpoint per user, we'll use the same
    // backend endpoints but they're scoped to "me". For others, we fall back to filtering follows.
    // Simpler: if viewing self, use me/* endpoints; otherwise we degrade gracefully.
    let fetchList;
    if (isMe) {
      const endpoint = kind === "followers" ? "/api/follows/me/followers" : "/api/follows/me/following";
      fetchList = fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then((r) => r.ok ? r.json() : []);
    } else {
      const endpoint = kind === "followers" ? `/api/follows/user/${userId}/followers` : `/api/follows/user/${userId}/following`;
      fetchList = fetch(`${API_URL}${endpoint}`).then((r) => r.ok ? r.json() : []);
    }
    Promise.all([fetchProfile, fetchList])
      .then(async ([prof, items]) => {
        setProfile(prof);
        setList(Array.isArray(items) ? items : []);
        // Fetch follow status for each user if logged in
        if (me && token && Array.isArray(items) && items.length) {
          const map = {};
          await Promise.all(items.map(async (u) => {
            if (u.id === me.id) return;
            try {
              const r = await fetch(`${API_URL}/api/follows/status/${u.id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (r.ok) map[u.id] = await r.json();
            } catch { /* ignore */ }
          }));
          setStatusMap(map);
        }
      })
      .finally(() => setLoading(false));
  }, [userId, kind, isMe, me, token]);

  const toggleFollow = async (targetId) => {
    if (!me) { toast.error("로그인이 필요합니다"); return; }
    if (targetId === me.id) return;
    setBusyId(targetId);
    try {
      const res = await fetch(`${API_URL}/api/follows/${targetId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "실패");
      setStatusMap((m) => ({ ...m, [targetId]: { ...(m[targetId] || {}), i_follow: data.following } }));
      toast.success(data.following ? "팔로우했어요 💞" : "팔로우 취소");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-20 pb-24" data-testid="follow-list-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-4"
          data-testid="follow-list-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> 뒤로
        </button>

        <div className="mb-5">
          <h1 className="font-serif text-2xl sm:text-3xl text-stone-900 tracking-tight" data-testid="follow-list-title">
            {profile?.username ? `${profile.username}님의 ${title}` : title}
          </h1>
          {!isMe && (
            <p className="text-sm text-stone-500 mt-1">
              {kind === "followers" ? "이 분을 응원하는 사람들" : "이 분이 응원하는 사람들"}
            </p>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" />
          </div>
        ) : list.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-8 h-8 mx-auto text-stone-300 mb-3" strokeWidth={1.5} />
            <p className="font-serif text-lg text-stone-500">
              {kind === "followers" ? "아직 팔로워가 없어요." : "아직 팔로잉하는 사람이 없어요."}
            </p>
            <p className="text-sm text-stone-400 mt-2">따뜻한 인연이 곧 시작될 거예요 🌿</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {list.map((u) => {
              const isMyself = me && u.id === me.id;
              const st = statusMap[u.id];
              return (
                <li
                  key={u.id}
                  className="bg-white rounded-2xl border border-stone-100 px-4 py-3 flex items-center gap-3 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                  data-testid={`follow-row-${u.id}`}
                >
                  <Link to={`/timeline/${u.id}`} className="flex items-center gap-3 flex-1 min-w-0 group">
                    {u.profile_image ? (
                      <img
                        src={u.profile_image.startsWith("http") ? u.profile_image : `${API_URL}${u.profile_image}`}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover border border-stone-100 shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-burgundy-500 to-rose-500 text-white flex items-center justify-center font-bold text-lg shrink-0">
                        {(u.username || "?").charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-stone-900 text-[15px] truncate group-hover:text-burgundy-700 transition-colors">
                        {u.username || "삭제된 사용자"}
                      </div>
                      <div className="text-xs text-stone-400 mt-0.5">
                        {u.followed_at ? new Date(u.followed_at).toLocaleDateString("ko-KR") : ""}
                      </div>
                    </div>
                  </Link>
                  {me && !isMyself && (
                    <button
                      onClick={() => toggleFollow(u.id)}
                      disabled={busyId === u.id}
                      className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${
                        st?.i_follow
                          ? "bg-stone-100 text-stone-700 hover:bg-stone-200"
                          : "bg-burgundy-600 text-white hover:bg-burgundy-700"
                      }`}
                      data-testid={`follow-toggle-${u.id}`}
                    >
                      {busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                        st?.i_follow ? <><UserCheck className="w-3.5 h-3.5" /> 팔로잉</> : <><UserPlus className="w-3.5 h-3.5" /> 팔로우</>
                      )}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
